/**
 * Contract Import Dialog - Simplified
 *
 * Import contracts from supplier quotation files (xlsx/pdf)
 * Features:
 * - File upload with drag-and-drop
 * - AI extraction using Qwen
 * - Auto create and confirm POs
 */

import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Upload,
  FileSpreadsheet,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import contractService from '../services/contractService';
import ContractImportReviewDialog from './ContractImportReviewDialog';

function ContractImportDialog({
  open,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [reviewContracts, setReviewContracts] = useState([]);

  const fileInputRef = useRef(null);

  const handleClose = useCallback(() => {
    setStep('upload');
    setFile(null);
    setUploading(false);
    setImporting(false);
    setError(null);
    setExtractionResult(null);
    setImportResult(null);
    setReviewContracts([]);
    onClose();
  }, [onClose]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/pdf',
        'image/jpeg',
        'image/png',
      ];
      if (!validTypes.includes(selectedFile.type) &&
          !selectedFile.name.match(/\.(xlsx|xls|pdf|jpg|jpeg|png)$/i)) {
        setError('请上传 Excel 或 PDF 文件');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect({ target: { files: [droppedFile] } });
    }
  }, [handleFileSelect]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      setError('请先选择文件');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await contractService.extractContract(file);
      if (result.success) {
        setExtractionResult(result);
        setStep('import');
      } else {
        setError(result.error || '文件解析失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '文件上传失败');
    } finally {
      setUploading(false);
    }
  }, [file]);

  const proceedWithImport = useCallback(async (contracts) => {
    setImporting(true);
    setError(null);

    try {
      const result = await contractService.importContracts(contracts);
      if (result.success) {
        setImportResult(result);
        setStep('done');
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        setError(result.error || '导入失败');
      }
    } catch (err) {
      setError(err.response?.data?.error || '导入失败');
    } finally {
      setImporting(false);
    }
  }, [onSuccess]);

  const handleImport = useCallback(async (extractionResult) => {
    const items = extractionResult?.items || [];
    const matchedCount = items.filter(i => i.matched_product_id).length;

    // If there are unmatched items, show review dialog directly
    if (matchedCount < items.length) {
      const contractData = [{
        supplier_name: extractionResult.supplier_name,
        quote_date: extractionResult.quote_date,
        items: items,
        source_file: file.name,
      }];
      setReviewContracts(contractData);
      setStep('review');
      return;
    }

    // All items matched, proceed with import
    const contractData = [{
      supplier_name: extractionResult.supplier_name,
      quote_date: extractionResult.quote_date,
      items: extractionResult.items,
      source_file: file.name,
    }];
    await proceedWithImport(contractData);
  }, [file, proceedWithImport]);

  const handleReviewConfirm = useCallback(async (updatedContracts) => {
    setStep('import');
    await proceedWithImport(updatedContracts);
  }, [proceedWithImport]);

  const getMatchBadge = (item) => {
    if (!item.matched_product_id) {
      return <span className="text-yellow-600 text-sm">待匹配</span>;
    }
    return <span className="text-green-600 text-sm">已匹配</span>;
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${file ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary'}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          className="hidden"
        />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            {file.name.match(/\.(xlsx|xls)$/i) ? (
              <FileSpreadsheet className="h-12 w-12 text-green-600" />
            ) : file.name.match(/\.pdf$/i) ? (
              <FileText className="h-12 w-12 text-red-600" />
            ) : (
              <FileText className="h-12 w-12 text-blue-600" />
            )}
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium">拖拽文件到此处或点击选择</p>
            <p className="text-sm text-muted-foreground">
              支持 Excel (.xlsx, .xls) 和 PDF 文件
            </p>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleClose}>
          取消
        </Button>
        <Button onClick={handleUpload} disabled={!file || uploading}>
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              解析中...
            </>
          ) : (
            '开始解析并导入'
          )}
        </Button>
      </div>
    </div>
  );

  const renderImportStep = () => {
    const items = extractionResult?.items || [];
    const matchedCount = items.filter(i => i.matched_product_id).length;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium">供应商</p>
            <p className="text-lg">{extractionResult?.supplier_name || '未知'}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">提取物料数</p>
            <p className="text-lg">{items.length}</p>
          </div>
          <div className="flex gap-2">
            <span className="text-green-600 text-sm">{matchedCount} 已匹配</span>
            <span className="text-yellow-600 text-sm">{items.length - matchedCount} 待匹配</span>
          </div>
        </div>

        <div className="border rounded-lg max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="text-left p-3">物料名称</th>
                <th className="text-left p-3">规格</th>
                <th className="text-right p-3">单价</th>
                <th className="text-center p-3">匹配状态</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className="border-t">
                  <td className="p-3">{item.material_name}</td>
                  <td className="p-3">{item.specification || '-'}</td>
                  <td className="p-3 text-right">
                    ¥{item.unit_price?.toFixed(2) || '0.00'}
                  </td>
                  <td className="p-3 text-center">
                    {getMatchBadge(item)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            匹配成功的物料将直接创建采购订单
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              取消
            </Button>
            <Button onClick={() => handleImport(extractionResult)} disabled={importing}>
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  导入中...
                </>
              ) : (
                '确认导入'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderDoneStep = () => (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-4 p-8">
        <CheckCircle2 className="h-16 w-16 text-green-600" />
        <h3 className="text-xl font-semibold">采购订单创建成功</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-muted rounded-lg text-center">
          <p className="text-3xl font-bold">{importResult?.created_count || 0}</p>
          <p className="text-sm text-muted-foreground">已创建的采购订单</p>
        </div>
        <div className="p-4 bg-muted rounded-lg text-center">
          <p className="text-3xl font-bold">{importResult?.updated_count || 0}</p>
          <p className="text-sm text-muted-foreground">已更新物料价格</p>
        </div>
      </div>

      {importResult?.unmatched_count > 0 && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {importResult.unmatched_count} 项物料未匹配到产品，已跳过
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-center gap-2">
        <Button variant="outline" onClick={handleClose}>
          关闭
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>导入合同</DialogTitle>
          <DialogDescription>
            上传供应商报价文件，自动提取数据并创建采购订单
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUploadStep()}
        {step === 'import' && renderImportStep()}
        {step === 'review' && (
          <ContractImportReviewDialog
            open={true}
            onClose={() => setStep('import')}
            contracts={reviewContracts}
            onConfirm={handleReviewConfirm}
            isCreating={importing}
          />
        )}
        {step === 'done' && renderDoneStep()}
      </DialogContent>
    </Dialog>
  );
}

ContractImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ContractImportDialog;
